import { all, call, delay, fork, put, select, takeLatest } from 'redux-saga/effects';
import { CalendarEvent } from 'shovel-lib/types';
import { applyUnplanResult } from '../calendar/operations';
import { SessionPlace } from '../undo/types';
import { closeTimerPill, moveTimerStart } from '@utils/calendar/markUsedCelebration';
import { blockEl, followBlocks, showDate, weekSide } from '@utils/calendar/floatToNow';
import { endUsedSave, startUsedSave } from '../calendar/pendingUsedSaves';
import { plannerApi, timerApi } from '../../api';
import * as actions from './actions';
import { StartTimerRequest } from './actions';
import { getErrorMessage } from '../../api/network';
import { apiCall, callApi } from '../common/operations';
import { applyTogether } from '../common/actions';
import { plannerApi as webPlannerApi } from '@api/plannerApi';
import { RootState } from '@state/types';
import * as semesterSelectors from '../semester/selectors';
import { StartTimerDto, TimerStatus } from 'shovel-lib/types';
import gaAnalytics, { AnalyticsEvent } from '../../config/gaAnalytics';
import * as timerSelectors from './selectors';
import { undoActions } from '../undo';
import { calendarActions } from '../calendar';
import { getElapsedSeconds, spanLabel } from '../../utils/timer';
import t from '../../i18n/t';

export function* start({ payload }: ReturnType<typeof actions.start.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const body: StartTimerDto & { startedFrom?: string; extendMinutes?: number } = {
    uid: payload.uid,
    entryType: payload.entryType,
    previousTimePassedInSeconds: payload.previousTimePassedInSeconds,
    extendMinutes: payload.extendMinutes,
    startedFrom: payload.startedFrom
  };
  // The session as it was, for Undo.
  const before = yield select((state: RootState) => state.calendar.plannedEvents[payload.uid]);
  const apiCall = call(timerApi.start, semesterId, body);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    //@ts-ignore
    yield put(actions.start.success({ ...response.data, taskId: payload.taskId }));
    yield fork(gaAnalytics.track, AnalyticsEvent.StartTimer.name);
    yield call(recordStart, payload, before);
  } else {
    yield put(actions.start.failure(getErrorMessage(response)));
  }
}

const placeOf = (id: number, event: { start: string | Date; end: string | Date }): SessionPlace => ({
  id,
  start: new Date(event.start).toISOString(),
  end: new Date(event.end).toISOString()
});

/**
 * Every ▶ can be undone (Petr, 2026-10-29: "if I start a timer and I press Command-Z, I should be able to undo"): the
 * timer stops, its seconds dropped, and the session goes back as it was, its place, its logged time and its tick, with
 * whatever else the start moved. Recorded once the timer is running: earlier, the start's own change would forget it at
 * once. A toast only when other sessions moved; otherwise silent, like a plain drag, for Cmd+Z and the Undo button.
 */
function* recordStart(payload: StartTimerRequest, before?: { start: string; end: string; used?: number; isUsed?: boolean }) {
  if (String(payload.entryType) !== 'PLANNED_TASK' || !before) return;
  const plannedTaskId = Number(payload.uid);
  const after = yield select((state: RootState) => state.calendar.plannedEvents[plannedTaskId]);
  const { places = [], to = [], createdIds = [], removedSessions = [], message } = payload.changes || {};
  const has = (list: SessionPlace[]) => list.some(place => place.id === plannedTaskId);
  yield put(
    undoActions.record({
      entry: {
        kind: 'timerStartRearranged',
        plannedTaskId,
        taskId: payload.taskId,
        // Its own block where it was before the start, if the start didn't move it (Add time grows it on the server).
        places: has(places) ? places : [...places, placeOf(plannedTaskId, before)],
        to: has(to) || !after ? to : [...to, placeOf(plannedTaskId, after)],
        createdIds,
        removedSessions,
        used: before.used || 0,
        isUsed: !!before.isUsed
      },
      message: message || t.TIMER_STARTED,
      silent: !message
    })
  );
}

function* startWithMoves({ payload }: ReturnType<typeof actions.startWithMoves>) {
  const { moves, pieces = [], removeIds = [], undoMessage, ...start } = payload;
  const own = moves.find(move => String(move.id) === String(start.uid));
  const { startDate, endDate, plannedEvents } = yield select((state: RootState) => state.calendar);
  let undo: { places: SessionPlace[]; to: SessionPlace[]; createdIds: number[]; removedSessions: unknown[] } | undefined;
  if (moves.length) {
    const places = moves.map(move => ({
      id: move.id,
      start: new Date(move.start).toISOString(),
      end: new Date(move.end).toISOString()
    }));
    const before = places
      .filter(place => plannedEvents[place.id])
      .map(place => ({ id: place.id, start: plannedEvents[place.id].start, end: plannedEvents[place.id].end }));
    // The block slides to now, and sessions it pushes slide along (floatToNow.ts): their new places are drawn at once,
    // and the server's answer and the timer starting wait for the slide, so their redraws don't break it up. In another
    // week the calendar turns to it, and it is there.
    const otherWeek = !!own && !!weekSide(own.start, startDate, endDate);
    const slid: Promise<void> =
      own && !otherWeek
        ? followBlocks([own.id, ...moves.filter(move => move !== own).map(move => move.id)])
        : Promise.resolve();
    if (otherWeek) showDate(own!.start);
    yield put(calendarActions.previewPlannedMoves(places));
    const semesterId = yield select(semesterSelectors.getId);
    const toSave = places.map(place => ({ id: Number(place.id), start: place.start, end: place.end }));
    // Sessions split or taken off (bumping the day down) go in one request, all or none.
    const rearranging = pieces.length > 0 || removeIds.length > 0;
    const save = rearranging
      ? call(apiCall as any, webPlannerApi.rearrangeSessions, semesterId, {
          moves: toSave,
          pieces: pieces.map(piece => ({
            fromId: Number(piece.fromId),
            start: new Date(piece.start).toISOString(),
            end: new Date(piece.end).toISOString()
          })),
          removeIds: removeIds.map(Number)
        })
      : call(apiCall as any, webPlannerApi.movePlannedTasks, semesterId, toSave);
    const [{ ok, ...response }] = yield all([save, call(() => slid)]);
    // Couldn't move them (outside the term, say): they go back, and the timer starts anyway, where the block is. Never
    // hold the timer up.
    if (ok) {
      const saved = yield select((state: RootState) => state.calendar.plannedEvents);
      const moved: CalendarEvent[] = (rearranging ? response.data?.moved : response.data) || [];
      const created: CalendarEvent[] = (rearranging && response.data?.created) || [];
      // One redraw for the lot: one each held the calendar up for half a second with a few sessions.
      yield put(
        applyTogether([
          ...moved.map(newEvent => calendarActions.resizePlannedTask.success({ oldEvent: saved[newEvent.id], newEvent })),
          ...(created.length ? [calendarActions.planTasks.success(created)] : [])
        ])
      );
      if (rearranging && response.data?.deletedPlannedTaskIds?.length) {
        yield fork(applyUnplanResult, response.data, true);
      }
      undo = {
        places: before.map(place => ({
          id: Number(place.id),
          start: new Date(place.start as any).toISOString(),
          end: new Date(place.end as any).toISOString()
        })),
        to: moved.map(event => ({
          id: Number(event.id),
          start: new Date(event.start as any).toISOString(),
          end: new Date(event.end as any).toISOString()
        })),
        createdIds: created.map(event => Number(event.id)),
        removedSessions: (rearranging && response.data?.removedSessions) || []
      };
    } else {
      yield put(calendarActions.previewPlannedMoves(before));
    }
    const block = own && blockEl(String(own.id));
    if (block) moveTimerStart(block.querySelector('.session-play') || block);
  }
  // Its Undo is recorded once the timer is running (recordStart), with what was moved here; with nothing saved, the
  // session is still where it was.
  yield put(actions.start.request({ ...(start as any), changes: undo && { ...undo, message: undoMessage } }));
}

function* toggle() {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(timerApi.toggle, semesterId);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    yield put(actions.toggle.success(response.data));
  } else {
    const errorMessage = getErrorMessage(response);
    yield call(handleTimerError, errorMessage);
    yield put(actions.toggle.failure(errorMessage));
  }
}

// Reset takes no question: it clears the whole session (Petr, 2026-10-16: "if you reset, you reset the whole thing"),
// and Undo puts it back.
function* cancel() {
  const semesterId = yield select(semesterSelectors.getId);
  const activeTimer = yield select(timerSelectors.getActiveTimer);
  const paused = activeTimer?.status !== TimerStatus.ACTIVE;
  const seconds = activeTimer ? getElapsedSeconds(activeTimer.timePassedInSeconds, activeTimer.startedInUtc, !paused) : 0;
  const apiCall = call(timerApi.cancel, semesterId);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    // Reset closes the top bar's pill smoothly too, before it goes.
    const closing = closeTimerPill();
    if (closing > 0) yield delay(closing);
    yield put(actions.cancel.success({ activeTimer, response: response.data }));
    if (response.data?.resetSession?.plannedTaskDto)
      yield put(calendarActions.markAsUsed.success(response.data.resetSession));
    if (activeTimer) {
      yield put(
        undoActions.record({
          entry: {
            kind: 'timerReset',
            plannedTaskId: Number(activeTimer.plannedTaskId),
            taskId: activeTimer.masterTaskId,
            previousUsed: activeTimer.sessionUsedMinutes || 0,
            seconds,
            paused
          },
          message: t.TIMER_CLEARED(spanLabel(seconds))
        })
      );
    }
  } else {
    const errorMessage = getErrorMessage(response);
    yield call(handleTimerError, errorMessage);
    yield put(actions.cancel.failure(errorMessage));
  }
}

export function* getCurrentTimer() {
  const semesterId = yield select(semesterSelectors.getId);
  const activeTimer = yield select(timerSelectors.getActiveTimer);
  const currentTimerState = activeTimer?.status || TimerStatus.INITIAL;

  if (!semesterId) return;
  const apiCall = call(timerApi.getBySemesterId, semesterId);
  const { ok, ...response } = yield call(callApi, apiCall);
  if (ok) {
    yield put(actions.getCurrentTimer.success(response.data));
    if (currentTimerState !== response.data.state && response.data.state === TimerStatus.PAUSED) {
      yield put(actions.toggle.success(response.data));
    }
    if (currentTimerState !== response.data.state && response.data.state === TimerStatus.INITIAL) {
      yield put(actions.cancel.success({ activeTimer, response: response.data }));
    }
  } else {
    yield put(actions.getCurrentTimer.failure(getErrorMessage(response)));
  }
}

/**
 * Manually changing active timer time
 */
function* updateTimerForTask(action: ReturnType<typeof actions.updateTimerForTask.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const apiCall = call(timerApi.updateTimerForTask, semesterId, action.payload);
  // A running timer's time on its way holds back marking its session used (pendingUsedSaves).
  const plannedTaskId = Number(action.payload.taskId);
  startUsedSave(plannedTaskId);
  let ok: boolean;
  let response: any;
  try {
    ({ ok, ...response } = yield call(callApi, apiCall));
  } finally {
    endUsedSave(plannedTaskId);
  }
  if (ok) {
    yield put(actions.updateTimerForTask.success(response.data));
  } else {
    const errorMessage = getErrorMessage(response);
    yield call(handleTimerError, errorMessage);
    yield put(actions.updateTimerForTask.failure(errorMessage));
  }
}

// Reset with no timer running (Petr, 2026-10-17): one block, one used time, so Reset clears the session's logged time
// and its tick too, back to untouched; Undo puts both back.
function* clearLoggedTime({ payload }: ReturnType<typeof actions.clearLoggedTime>) {
  const { plannedTaskId, used, isUsed } = payload;
  const semesterId = yield select(semesterSelectors.getId);
  if (isUsed) {
    const unticked = yield apiCall(plannerApi.markAsUsed, semesterId, plannedTaskId, { isUsed: false } as any);
    if (!unticked.ok) return;
    yield put(calendarActions.markAsUsed.success(unticked.data));
  }
  const cleared = yield apiCall(plannerApi.updatePlannedTask, semesterId, plannedTaskId, { used: 0 } as any);
  if (!cleared.ok) return;
  yield put(calendarActions.updatePlannedTask.success({ data: cleared.data }));
  yield put(
    undoActions.record({
      entry: { kind: 'sessionCleared', plannedTaskId, used, isUsed },
      message: t.TIMER_CLEARED(spanLabel(used * 60))
    })
  );
}

// Two "Still studying?" check-ins unanswered (Petr, 2026-10-16): the timer pauses at the moment the second one came,
// so the time since isn't counted; nothing is saved. Also when the tab comes back after the laptop slept.
function* pauseAt({ payload: seconds }: ReturnType<typeof actions.pauseAt>) {
  const semesterId = yield select(semesterSelectors.getId);
  const activeTimer = yield select(timerSelectors.getActiveTimer);
  if (!activeTimer || activeTimer.status !== TimerStatus.ACTIVE) return;
  const { ok, ...response } = yield call(callApi, call(timerApi.toggle, semesterId));
  if (!ok) return;
  yield put(actions.toggle.success(response.data));
  yield put(actions.updateTimerForTask.request({ taskId: activeTimer.plannedTaskId, timePassedInSeconds: seconds } as any));
}

function* handleTimerError(error: string) {
  if (error === 'STARTED_STOPWATCH_DOES_NOT_EXIST') {
    yield put(actions.clearSession());
  }
}

export default function*() {
  yield all([
    takeLatest(actions.start.request, start),
    takeLatest(actions.startWithMoves, startWithMoves),
    takeLatest(actions.toggle.request, toggle),
    takeLatest(actions.pauseAt, pauseAt),
    takeLatest(actions.clearLoggedTime, clearLoggedTime),
    takeLatest(actions.cancel.request, cancel),
    takeLatest(actions.getCurrentTimer.request, getCurrentTimer),
    takeLatest(actions.updateTimerForTask.request, updateTimerForTask)
  ]);
}
